import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import {
  getEndedAtTs,
  getFirstStartedAtTs,
  getPausedAtTs,
} from "@intelligems/ig-analytics/experience-status";
import {
  ensureNumber,
  officialCategoryName,
} from "@intelligems/ig-utils/common";
import { Popover, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { getTinybirdActiveAnalyticsFilters } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useOrgTimezone } from "@src/api/organizations/get-organization";
import { DashboardPageName } from "@src/api/types/dashboards";
import { DateRangePicker } from "@src/features/analytics/analyticsFilters/DateRangePicker";
import {
  getInitialStartFilterTime,
  getLastEndFilterTime,
} from "@src/features/analytics/analyticsFilters/utils";
import {
  getDefaultHiddenEndTime,
  getDefaultPersonalizationStartTime,
} from "@src/store/helpers";
import {
  getExperienceAnalyticsFilters,
  setDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import { mpClickAnalyticsFilterSaveButton } from "@src/utils/mixpanel";
import {
  calculateProductCategories,
  getAnalyticsPageType,
} from "@src/utils/mpUtils";

interface AnalyticsDateRangeModalProps {
  anchorEl: HTMLElement | null;
  setAnchorEl: (anchorEl: HTMLElement | null) => void;
  experience: ExperienceFe;
}

export const AnalyticsDateRangeModal = ({
  anchorEl,
  setAnchorEl,
  experience,
}: AnalyticsDateRangeModalProps) => {
  const theme = useTheme();
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const maxZScoreConfig = useConfiguration("max_z_score");
  const defaultStartTime = getInitialStartFilterTime(
    experience,
    dashboardFilters
  );
  const timezone = useOrgTimezone();
  const defaultEndTime = getLastEndFilterTime(experience, dashboardFilters);
  const [startTime, setStartTime] = useImmer<number | null>(defaultStartTime);
  const [endTime, setEndTime] = useImmer<number | null>(defaultEndTime);

  const handleClose = () => {
    setStartTime(defaultStartTime);
    setEndTime(null);
    setAnchorEl(null);
  };
  const [isStartValid, setIsStartValid] = useImmer<boolean>(true);
  const [isEndValid, setIsEndValid] = useImmer<boolean>(true);

  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const onOkClick = () => {
    dispatch(setDashboardFilter({ ...dashboardFilters, endTime, startTime }));
    const getTbActiveFilters = getTinybirdActiveAnalyticsFilters(
      {
        ...dashboardFilters,
        endTime,
        startTime,
      },
      maxZScoreConfig!.value
    );
    if (Object.keys(getTbActiveFilters).length > 0) {
      mpClickAnalyticsFilterSaveButton(
        getTbActiveFilters,
        getAnalyticsPageType(experience.category),
        dashboardFilters.pageName as DashboardPageName,
        calculateProductCategories(experience),
        officialCategoryName(experience.category, false),
        experience.id
      );
    }
    handleClose();
  };

  const onResetClick = () => {
    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        endTime:
          experience.category === "experiment"
            ? null
            : getDefaultHiddenEndTime(experience),
        startTime:
          experience.category === "experiment"
            ? null
            : getDefaultPersonalizationStartTime(experience, timezone),
      })
    );
    handleClose();
  };

  const onTimeChange = (
    newValue: DateTime | null,
    filterType: "start" | "end"
  ) => {
    if (filterType === "start") {
      setStartTime((draft) => {
        draft = newValue?.toMillis() ?? null;
        return draft;
      });
    } else {
      setEndTime((draft) => {
        draft = newValue?.toMillis() ?? null;
        return draft;
      });
    }
  };

  useEffect(() => {
    console.log("Start is valid", isStartValid);
    console.log("End is valid", isEndValid);
    console.log("!isEndValid || !isStartValid", !isEndValid || !isStartValid);
  }, [isEndValid, isStartValid]);

  return (
    <Popover
      onClose={handleClose}
      anchorEl={anchorEl}
      open={open}
      sx={{ borderRadius: "5px" }}
      id={"date-range"}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "5px",
            boxShadow: theme.igShadows.low,
          },
          elevation: 0,
        },
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <IgPaper sx={{ padding: "16px 24px" }}>
        <Grid container direction={"column"} rowSpacing={4}>
          <Grid>
            <IgTypography
              sx={{
                fontWeight: 600,
              }}
            >
              Date Range
            </IgTypography>
          </Grid>
          <Grid>
            <DateRangePicker
              onStartError={(error) => {
                // Error event is triggered when date becomes invalid and when it turns valid again
                setIsStartValid(Boolean(!error));
              }}
              onEndError={(error) => {
                // Error event is triggered when date becomes invalid and when it turns valid again
                setIsEndValid(Boolean(!error));
              }}
              startFilter={DateTime.fromMillis(ensureNumber(startTime) ?? 0)}
              endFilter={
                endTime ? DateTime.fromMillis(ensureNumber(endTime)) : null
              }
              onTimeChange={onTimeChange}
              resourceMinTime={
                getFirstStartedAtTs(experience.experienceActions) ?? 0
              }
              resourceMaxTime={
                experience.status === "ended"
                  ? (getEndedAtTs(experience.experienceActions)?.actionTs ?? 0)
                  : experience.status === "paused"
                    ? (getPausedAtTs(experience.experienceActions)?.actionTs ??
                      0)
                    : undefined
              }
            />
          </Grid>
          <Grid container direction={"row"} justifyContent={"space-between"}>
            <Grid>
              <IgButton
                size={"small"}
                color={"secondary"}
                onClick={onResetClick}
                sx={{ minHeight: "30px", maxHeight: "30px" }}
              >
                {experience.category === "personalization" ? "Reset" : "Clear"}
              </IgButton>
            </Grid>
            <Grid>
              <IgButton
                disabled={!isEndValid || !isStartValid}
                sx={{ minHeight: "30px", maxHeight: "30px" }}
                variant={"contained"}
                size={"small"}
                onClick={onOkClick}
              >
                Ok
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      </IgPaper>
    </Popover>
  );
};
